import { ArrowUpDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useGetMyInvestigationsQuery } from '@/store/api/apiSlice';
import { Investigation, InvestigationStatus } from '@/types';

const CompletedInvestigationTable = () => {
  const navigate = useNavigate();
  const { data: allInvestigations = [], isLoading } = useGetMyInvestigationsQuery();

  // Filter for completed investigations
  const investigations = allInvestigations.filter(inv =>
    inv.status === InvestigationStatus.COMPLETED
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getEmployeeName = (inv: Investigation) => {
    return `${inv.employeeFirstName}${inv.employeeMiddleName ? ' ' + inv.employeeMiddleName : ''} ${inv.employeeLastName}`;
  };

  return (
    <div className="bg-white rounded-2xl md:rounded-3xl shadow-sm overflow-hidden p-4 md:p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-start">Completed Investigation</h2>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden flex-1 overflow-y-auto space-y-3">
        {isLoading ? (
          <p className="text-center text-gray-500 py-8">Loading...</p>
        ) : investigations.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No completed investigations</p>
        ) : (
          investigations.map((investigation) => (
            <button
              type="button"
              key={investigation.id}
              onClick={() => navigate(`/investigations/${investigation.id}`)}
              className="w-full text-left border border-gray-100 rounded-xl p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">{getEmployeeName(investigation)}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-100 px-2 py-0.5 rounded-lg font-medium text-xs capitalize"
                >
                  {investigation.checkType}
                </Badge>
                <span className="text-xs text-gray-500">{formatDate(investigation.createdAt)}</span>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto overflow-y-auto -mx-6 flex-1">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  Employee Name
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  Check Type
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </th>
              <th className="px-6 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  Date Created
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : investigations.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No completed investigations
                </td>
              </tr>
            ) : (
              investigations.map((investigation, index) => (
                <tr
                  key={investigation.id}
                  className={`border-b border-gray-50 last:border-b-0 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                  }`}
                >
                  <td className="px-6 py-4 text-start">
                    <span className="text-sm text-gray-900 text-start">{getEmployeeName(investigation)}</span>
                  </td>
                  <td className="px-6 py-4 text-start">
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-100 px-4 py-1 rounded-lg font-medium text-sm capitalize"
                    >
                      {investigation.checkType}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-start">
                    <span className="text-sm text-gray-700 text-start">{formatDate(investigation.createdAt)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      onClick={() => navigate(`/investigations/${investigation.id}`)}
                      className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-xl text-sm font-medium whitespace-nowrap"
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompletedInvestigationTable;
